import React, { useState, useEffect, useRef } from "react";
import "./chooseride.css";
import { mapStyles } from "./mapStyles";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../AuthContext";
import { ref, get, query, orderByChild, equalTo } from "firebase/database";
import { db } from "../../firebase";
import locationPickupIcon from "../../assets/locationpickup.svg";
import locationDropoffIcon from "../../assets/locationdropoff.svg";

const getCategoryIcon = (category) => {
  switch (category) {
    case 'Attractions':
      return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s-8-4.5-8-11.8A8 8 0 0 1 12 2a8 8 0 0 1 8 8.2c0 7.3-8 11.8-8 11.8z"></path><circle cx="12" cy="10" r="3"></circle></svg>;
    case 'Transport':
      return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="4" width="14" height="16" rx="2" ry="2"></rect><line x1="9" y1="18" x2="9" y2="18"></line><line x1="15" y1="18" x2="15" y2="18"></line><line x1="5" y1="10" x2="19" y2="10"></line></svg>;
    case 'Hotels':
      return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2 22V4a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v18"></path><path d="M10 22v-4a2 2 0 0 1 2-2v0a2 2 0 0 1 2 2v4"></path><path d="M6 6h4"></path><path d="M6 10h4"></path><path d="M14 6h4"></path><path d="M14 10h4"></path></svg>;
    case 'Entertainment':
      return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18"></rect><line x1="7" y1="2" x2="7" y2="22"></line><line x1="17" y1="2" x2="17" y2="22"></line><line x1="2" y1="12" x2="22" y2="12"></line><line x1="2" y1="7" x2="7" y2="7"></line><line x1="2" y1="17" x2="7" y2="17"></line><line x1="17" y1="17" x2="22" y2="17"></line><line x1="17" y1="7" x2="22" y2="7"></line></svg>;
    case 'Restaurants':
      return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8h1a4 4 0 0 1 0 8h-1"></path><path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"></path><line x1="6" y1="1" x2="6" y2="4"></line><line x1="10" y1="1" x2="10" y2="4"></line><line x1="14" y1="1" x2="14" y2="4"></line></svg>;
    default:
      return null;
  }
};

function ChooseRide() {
  const [pickup, setPickup] = useState("");
  const [city, setCity] = useState("");
  const [dropoff, setDropoff] = useState("");
  const [pickupPredictions, setPickupPredictions] = useState([]);
  const [pickupCoords, setPickupCoords] = useState(null);
  const [dropoffCoords, setDropoffCoords] = useState(null);
  const [dropoffPredictions, setDropoffPredictions] = useState([]);
  const [activeInput, setActiveInput] = useState(null);
  const [isCityModalOpen, setIsCityModalOpen] = useState(false);
  const [exploreCategory, setExploreCategory] = useState('Attractions');
  const [isExploreModalOpen, setIsExploreModalOpen] = useState(false);
  const [isLocationRequiredModalOpen, setIsLocationRequiredModalOpen] = useState(false);
  const [newCityInput, setNewCityInput] = useState("");
  const [cityPredictions, setCityPredictions] = useState([]);
  const [cityBounds, setCityBounds] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [places, setPlaces] = useState([]);
  const [placesStatus, setPlacesStatus] = useState('IDLE'); // IDLE, LOADING, SUCCESS, ERROR
  const [selectedPlaceId, setSelectedPlaceId] = useState(null);
  const [placesError, setPlacesError] = useState(null);
  const [map, setMap] = useState(null);
  const autocompleteService = useRef(null);
  const geocoder = useRef(null);
  const mapRef = useRef(null);
  const infoWindowRef = useRef(null);
  const markersRef = useRef([]);
  const navigate = useNavigate();
  const [activeTripForRider, setActiveTripForRider] = useState(null);
  const { currentUser } = useAuth();
  const location = useLocation();

  useEffect(() => {
    // If we are already on the /go page, don't run this check to avoid loops
    if (location.pathname.startsWith('/go') || !currentUser) {
      return;
    }

    const checkForActiveTrip = async () => {
      const riderId = currentUser.uid;
      const tripRequestsRef = ref(db, 'tripRequests');
      const activeRequestQuery = query(tripRequestsRef, orderByChild('riderId'), equalTo(riderId));

      try {
        const snapshot = await get(activeRequestQuery);
        if (!snapshot.exists()) {
          return; // No active requests for this rider.
        }

        const activeRequests = snapshot.val();
        for (const requestId in activeRequests) {
          const request = activeRequests[requestId];
          const activeStatuses = ['searching', 'found', 'driving_to_pickup', 'on_trip', 'driving_to_destination', 'driver_arrived'];

          if (activeStatuses.includes(request.status)) {
            // Found an active request or trip.
            if (request.tripId) {
              const tripSnapshot = await get(ref(db, `trips/${request.tripId}`));
              if (tripSnapshot.exists()) {
                const tripData = tripSnapshot.val();
                setActiveTripForRider({ ...tripData, id: request.tripId });
                return; // Exit after finding and handling the active trip.
              }
            } else if (request.status === 'searching' || request.status === 'found') {
              // This is a request that hasn't been fully matched to a trip yet.
              const tripToPass = { ...request, id: requestId, destination: request.destination };
              setActiveTripForRider(tripToPass);
              return; // Exit after finding and handling the active request.
            }
          }
        }
      } catch (error) {
        console.error("Error checking for active trip on chooseride page:", error);
      }
    };

    checkForActiveTrip();
  }, [currentUser, location.pathname]);

  useEffect(() => {
    if (window.google && window.google.maps) {
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
      geocoder.current = new window.google.maps.Geocoder();
    }
  }, []);

  const handleGetCurrentLocation = () => {
    const defaultLocation = { lat: 6.5244, lng: 3.3792 }; // Lagos

    const handleLocationError = (error) => {
      console.error("Geolocation error:", error.message);
      setCity("Lagos"); // Set a default city on error
      setUserLocation(defaultLocation); // Set a default location for map functionality
    };

    // Try to get user's current location for pickup
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          const newUserLocation = { lat: latitude, lng: longitude };
          setUserLocation(newUserLocation);
          setPickupCoords(newUserLocation);
          if (geocoder.current) {
            geocoder.current.geocode(
              { location: newUserLocation },
              (results, status) => {
                if (status === "OK" && results && results.length > 0) {
                  setPickup(results[0].formatted_address);
                  // Extract city more robustly
                  const cityComponent =
                    results[0].address_components.find(c => c.types.includes('locality')) ||
                    results[0].address_components.find(c => c.types.includes('administrative_area_level_2')) ||
                    results[0].address_components.find(c => c.types.includes('administrative_area_level_1'));
  
                  if (cityComponent) {
                    setCity(cityComponent.long_name);
                  } else {
                    handleLocationError({ message: 'Could not determine city from coordinates.' });
                  }

                  // Find a broader result for bounds
                  const boundsResult = results.find(r => r.types.includes('locality')) || results.find(r => r.types.includes('administrative_area_level_1'));
                  if (boundsResult && boundsResult.geometry.viewport) {
                    setCityBounds(boundsResult.geometry.viewport);
                  } else {
                    setCityBounds(null);
                  }
                } else {
                  console.error(`Reverse geocoding failed due to: ${status}`);
                  handleLocationError({ message: `Reverse geocoding failed: ${status}` });
                }
              }
            );
          }
        },
        handleLocationError,
        { timeout: 10000 } // Add a 10-second timeout
      );
    } else {
      handleLocationError({ message: "Geolocation is not supported by this browser." });
    }
  };

  useEffect(() => {
    handleGetCurrentLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isExploreModalOpen && userLocation && !map) {
      const mapInstance = new window.google.maps.Map(mapRef.current, {
        center: userLocation,
        zoom: 12,
        disableDefaultUI: true,
        styles: mapStyles,
        gestureHandling: 'none',
        zoomControl: false
      });
      setMap(mapInstance);
      // Create a single InfoWindow instance to be reused.
      if (window.google && !infoWindowRef.current) {
        infoWindowRef.current = new window.google.maps.InfoWindow();
      }
    }
  }, [isExploreModalOpen, userLocation, map]);

  useEffect(() => {
    if (map && exploreCategory && userLocation) {
      const getPlacesApiErrorMessage = (status) => {
        switch (status) {
          case 'INVALID_REQUEST':
            return 'The request was invalid. This is often a developer error.';
          case 'NOT_FOUND':
            return 'The location specified could not be found in the Places database.';
          case 'OVER_QUERY_LIMIT':
            return 'You have exceeded your usage limit for the Places API. Please check your Google Cloud console.';
          case 'REQUEST_DENIED':
            return 'Your request was denied. Please check your API key, billing status, and ensure the "Places API" is enabled in your Google Cloud console.';
          case 'UNKNOWN_ERROR':
            return 'An unknown server error occurred. Please try again later.';
          default:
            return `An unexpected error occurred. Status: ${status}`;
        }
      }

      // When category changes, close any open info window.
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
      }

      markersRef.current.forEach(marker => marker.setMap(null));
      markersRef.current = [];
      setPlaces([]);
      setSelectedPlaceId(null);
      setPlacesStatus('LOADING');
      setPlacesError(null);

      const searchNearbyPlaces = async () => {
        try {
          const { Place } = await window.google.maps.importLibrary("places");
          const categoryMap = {
            'Attractions': 'tourist_attraction',
            'Transport': 'transit_station',
            'Hotels': 'lodging',
            'Entertainment': 'movie_theater',
            'Restaurants': 'restaurant'
          };
          const request = {
            fields: ['id', 'displayName', 'formattedAddress', 'location', 'rating', 'photos'],
            locationRestriction: {
              center: userLocation,
              radius: 5000,
            },
            includedTypes: [categoryMap[exploreCategory] || 'point_of_interest'],
            maxResultCount: 20,
          };

          const { places: nearbyPlaces } = await Place.searchNearby(request);

          const adaptedPlaces = nearbyPlaces.map(place => ({
            place_id: place.id,
            name: place.displayName,
            vicinity: place.formattedAddress,
            rating: place.rating,
            geometry: { location: place.location },
            photoUrl: place.photos && place.photos.length > 0 ? place.photos[0].getURI({ maxWidth: 100 }) : null,
          }));

          setPlaces(adaptedPlaces);
          setPlacesStatus('SUCCESS');
        } catch (error) {
          setPlacesStatus('ERROR');
          const status = error.code;
          const errorMessage = getPlacesApiErrorMessage(status);
          setPlacesError(errorMessage);
          console.error(`Places search failed with error:`, error);
        }
      };

      searchNearbyPlaces();
    }
  }, [map, exploreCategory, userLocation]);

  useEffect(() => {
    if (map && places.length > 0) {
      const defaultIcon = {
        url: locationPickupIcon,
        scaledSize: new window.google.maps.Size(24, 35),
        anchor: new window.google.maps.Point(12, 35),
      };
      const bounds = new window.google.maps.LatLngBounds();
      const newMarkers = places.map(place => {
        if (place.geometry?.location) {
          const marker = new window.google.maps.Marker({
            map,
            position: place.geometry.location,
            title: place.name,
            icon: defaultIcon,
          });
          marker.place_id = place.place_id;

          marker.addListener('click', () => {
            const content = `
              <div style="font-family: sans-serif; max-width: 200px;">
                <h4 style="margin: 0 0 4px 0; font-size: 16px; font-weight: 600;">${place.name}</h4>
                <p style="margin: 0; font-size: 14px; color: #666;">${place.vicinity}</p>
              </div>
            `;
            if (infoWindowRef.current) {
              infoWindowRef.current.setContent(content);
              infoWindowRef.current.open({
                anchor: marker,
                map,
              });
            }
            setSelectedPlaceId(place.place_id);
          });

          bounds.extend(place.geometry.location);
          return marker;
        }
        return null;
      }).filter(Boolean);
      markersRef.current = newMarkers;
      map.fitBounds(bounds);
    }
  }, [map, places]);

  useEffect(() => {
    if (map && markersRef.current.length > 0) {
      const defaultIcon = {
        url: locationPickupIcon,
        scaledSize: new window.google.maps.Size(24, 35),
        anchor: new window.google.maps.Point(12, 35),
      };
      const selectedIcon = {
        url: locationDropoffIcon,
        scaledSize: new window.google.maps.Size(32, 33),
        anchor: new window.google.maps.Point(16, 33),
      };
      markersRef.current.forEach(marker => {
        marker.setIcon(marker.place_id === selectedPlaceId ? selectedIcon : defaultIcon);
      });
    }
  }, [selectedPlaceId, map]);

  const handleInputChange = (e, type) => {
    const value = e.target.value;
    if (type === "pickup") {
      setPickup(value);
    } else if (type === "dropoff") {
      setDropoff(value);
    } else if (type === "city") {
      setNewCityInput(value);
    }
    setActiveInput(type);

    if (!value) {
      if (type === "pickup") setPickupPredictions([]);
      else if (type === "dropoff") setDropoffPredictions([]);
      else if (type === "city") setCityPredictions([]);
      return;
    }

    if (autocompleteService.current) {
      let options;

      if (type === 'city') {
        options = { 
          input: value, 
          types: ['(regions)'], 
          componentRestrictions: { country: 'ng' } 
        };
      } else { // pickup or dropoff
        options = {
          input: value,
          componentRestrictions: { country: 'ng' }
        };
        if (cityBounds) {
          options.bounds = cityBounds;
          options.strictBounds = true; // This makes it a restriction
        }
      }

      autocompleteService.current.getPlacePredictions(
        options,
        (results, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
            if (type === "pickup") {
              setPickupPredictions(results);
            } else if (type === "dropoff") {
              setDropoffPredictions(results);
            } else if (type === "city") {
              const filteredResults = results.filter(p =>
                p.types.includes('administrative_area_level_1') || 
                p.types.includes('locality')
              );
              setCityPredictions(filteredResults);
            }
          } else {
            if (status !== window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
              console.error('PlacesService.getPlacePredictions failed with status: ' + status);
            }
            if (type === "pickup") setPickupPredictions([]);
            else if (type === "dropoff") setDropoffPredictions([]);
            else if (type === "city") setCityPredictions([]);
          }
        }
      );
    }
  };

  const handlePredictionSelect = (prediction, type) => {
    if (type === "pickup") {
      setPickup(prediction.description);
      setPickupPredictions([]);
    } else if (type === "dropoff") {
      setDropoff(prediction.description);
      setDropoffPredictions([]);
    } else if (type === "city") {
      setNewCityInput("");
      setPickup(""); // Clear pickup address on city change
      setPickupCoords(null);
      setCityPredictions([]);
      setIsCityModalOpen(false);
    }

    if (geocoder.current) {
      geocoder.current.geocode({ placeId: prediction.place_id }, (results, status) => {
        if (status === "OK" && results[0]?.geometry) {
          const geometry = results[0].geometry;
          const newLocation = {
            lat: geometry.location.lat(),
            lng: geometry.location.lng()
          };

          if (type === 'pickup') {
            setPickupCoords(newLocation);
          } else if (type === 'dropoff') {
            setDropoffCoords(newLocation);
          } else if (type === 'city') {
            const cityName = prediction.terms[0].value;
            setCity(cityName);
            setUserLocation(newLocation);
            if (geometry.viewport) {
              setCityBounds(geometry.viewport);
            }
          }
        }
      });
    }
    setActiveInput(null);
  };

  const handlePlaceCardClick = (place) => {
    if (!map || !place.geometry?.location) return;

    map.panTo(place.geometry.location);

    const correspondingMarker = markersRef.current.find(
      (marker) => marker.place_id === place.place_id
    );

    if (correspondingMarker) {
      // Trigger the marker's existing click listener to open the InfoWindow
      new window.google.maps.event.trigger(correspondingMarker, 'click');
    }
  };

  const handleExplorePlace = async (e, place) => {
    e.stopPropagation();
    
    if (!pickup) {
      alert("Please enter or wait for your present location to load.");
      return;
    }

    const destAddress = place.vicinity || place.name;
    const destCoords = place.geometry?.location ? { 
      lat: typeof place.geometry.location.lat === 'function' ? place.geometry.location.lat() : place.geometry.location.lat, 
      lng: typeof place.geometry.location.lng === 'function' ? place.geometry.location.lng() : place.geometry.location.lng 
    } : null;

    const geocodeAddress = (address) => new Promise((resolve, reject) => {
      if (!geocoder.current) return reject(new Error("Geocoder not initialized."));
      geocoder.current.geocode({ address }, (results, status) => {
        if (status === "OK" && results[0]) {
          const location = results[0].geometry.location;
          resolve({ lat: location.lat(), lng: location.lng() });
        } else {
          reject(new Error(`Geocoding failed for "${address}": ${status}`));
        }
      });
    });

    try {
      const pCoords = pickupCoords || await geocodeAddress(pickup);
      const dCoords = destCoords || await geocodeAddress(destAddress);

      if (!pickupCoords) setPickupCoords(pCoords);

      const encodedPickup = encodeURIComponent(pickup);
      const encodedDropoff = encodeURIComponent(destAddress);

      handleCloseExploreModal();

      navigate(`/go/from/${encodedPickup}/to/${encodedDropoff}`, {
        state: {
          pickup: { address: pickup, coords: pCoords },
          dropoff: { address: destAddress, coords: dCoords },
          city: city,
        },
      });
    } catch (error) {
      console.error("Error setting coordinates for exploration:", error);
      alert("Could not get coordinates for the selected location.");
    }
  };

  const handleSeePrice = async () => {
    if (!pickup || !dropoff) {
      setIsLocationRequiredModalOpen(true);
      return;
    }

    const geocodeAddress = (address) => new Promise((resolve, reject) => {
      if (!geocoder.current) return reject(new Error("Geocoder not initialized."));
      geocoder.current.geocode({ address }, (results, status) => {
        if (status === "OK" && results[0]) {
          const location = results[0].geometry.location;
          resolve({ lat: location.lat(), lng: location.lng() });
        } else {
          reject(new Error(`Geocoding failed for "${address}": ${status}`));
        }
      });
    });

    try {
      const pCoords = pickupCoords || await geocodeAddress(pickup);
      const dCoords = dropoffCoords || await geocodeAddress(dropoff);

      // Update state just in case they were geocoded now
      if (!pickupCoords) setPickupCoords(pCoords);
      if (!dropoffCoords) setDropoffCoords(dCoords);

      const encodedPickup = encodeURIComponent(pickup);
      const encodedDropoff = encodeURIComponent(dropoff);

      navigate(`/go/from/${encodedPickup}/to/${encodedDropoff}`, {
        state: {
          pickup: { address: pickup, coords: pCoords },
          dropoff: { address: dropoff, coords: dCoords },
          city: city,
        },
      });
    } catch (error) {
      console.error("Error getting coordinates:", error);
      alert("Could not find coordinates for one or both locations. Please select a valid address from the suggestions or try a different address.");
    }
  };

  const handleCloseExploreModal = () => {
    setIsExploreModalOpen(false);
    setMap(null);
    setPlaces([]);
    setPlacesStatus('IDLE');
    setSelectedPlaceId(null);
    if (infoWindowRef.current) infoWindowRef.current.close();
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];
  };

  const handleSuggestedCitySelect = (cityName) => {
    setCity(cityName);
    if (geocoder.current) {
      geocoder.current.geocode({ address: `${cityName}, Nigeria` }, (results, status) => {
        if (status === 'OK' && results[0]?.geometry) {
          const geometry = results[0].geometry;
          const newLocation = {
            lat: geometry.location.lat(),
            lng: geometry.location.lng()
          };
          setUserLocation(newLocation);
          if (geometry.viewport) {
            setCityBounds(geometry.viewport);
          } else {
            setCityBounds(null);
          }
        }
      });
    }
    setPickup(""); // Clear pickup address on city change
    setPickupCoords(null);
    setIsCityModalOpen(false);
    setNewCityInput("");
    setCityPredictions([]);
    setActiveInput(null);
  };

  const handleContinueRide = () => {
    if (!activeTripForRider) return;

    const trip = activeTripForRider;
    // Ensure destination is available, which it should be from the request/trip data.
    const destination = trip.destination || dropoff;

    const encodedPickup = encodeURIComponent(trip.pickup.address);
    const encodedDropoff = encodeURIComponent(destination.address);

    navigate(`/go/from/${encodedPickup}/to/${encodedDropoff}`, {
      state: { activeTrip: trip },
    });
  };

  const exploreCategories = ['Attractions', 'Transport', 'Hotels', 'Entertainment', 'Restaurants'];

  return (
    <div className='chooseride'>
      <div className={`chooseride-container ${currentUser ? 'has-suggestions' : ''}`}>
        <div className="ride-request-box">
          <div className="city-selector">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>
            <div className="city-selector-group">
              <span>{city || 'Finding location...'}</span>
              <button className="change-city-btn" onClick={() => setIsCityModalOpen(true)}>Change city</button>
            </div>
          </div>
          <div className="input-group">
            <div className="input-timeline-icons">
              <div className="timeline-icon-start"></div>
              <div className="timeline-line"></div>
              <div className="timeline-icon-end"></div>
            </div>
            <div className="input-fields-wrapper">
              <div className="input-wrapper">
              <input
                type="text"
                className="location-input"
                placeholder="Enter pickup location"
                value={pickup}
                onChange={(e) => handleInputChange(e, "pickup")}
                onFocus={() => setActiveInput("pickup")}
              />
                <div className="input-adornments">
                {pickup && (
                  <button 
                    onClick={() => { setPickup(''); setPickupPredictions([]); }}
                    className="clear-input-btn"
                    title="Clear pickup location"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                  </button>
                )}
                  <button onClick={handleGetCurrentLocation} className="current-location-icon-btn" title="Use current location">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path><path d="M12 16C14.2091 16 16 14.2091 16 12C16 9.79086 14.2091 8 12 8C9.79086 8 8 9.79086 8 12C8 14.2091 9.79086 16 12 16Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path></svg>
                </button>
              </div>
              {activeInput === "pickup" && pickupPredictions.length > 0 && (
                <ul className="predictions-dropdown">
                  {pickupPredictions.map((p) => (
                    <li key={p.place_id} className="prediction-item" onClick={() => handlePredictionSelect(p, "pickup")}>
                      <div className="prediction-icon"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg></div>
                      {p.structured_formatting ? (
                        <div className="prediction-text">
                            <span className="prediction-main-text">{p.structured_formatting.main_text}</span>
                            <span className="prediction-secondary-text">{p.structured_formatting.secondary_text}</span>
                        </div>
                      ) : (
                        <span>{p.description}</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              </div>
              <div className="input-wrapper">
              <input
                type="text"
                className="location-input"
                placeholder="Enter destination"
                value={dropoff}
                onChange={(e) => handleInputChange(e, "dropoff")}
                onFocus={() => setActiveInput("dropoff")}
              />
                <div className="input-adornments">
                {dropoff && (
                  <button 
                    onClick={() => { setDropoff(''); setDropoffPredictions([]); }}
                    className="clear-input-btn"
                    title="Clear destination"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                  </button>
                )}
              </div>
              {activeInput === "dropoff" && dropoffPredictions.length > 0 && (
                <ul className="predictions-dropdown">
                  {dropoffPredictions.map((p) => (
                    <li key={p.place_id} className="prediction-item" onClick={() => handlePredictionSelect(p, "dropoff")}>
                      <div className="prediction-icon"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg></div>
                      {p.structured_formatting ? (
                        <div className="prediction-text">
                            <span className="prediction-main-text">{p.structured_formatting.main_text}</span>
                            <span className="prediction-secondary-text">{p.structured_formatting.secondary_text}</span>
                        </div>
                      ) : (
                        <span>{p.description}</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            </div>
          </div>
          <div className="ride-options">
            {activeTripForRider ? (
              <button className="request-ride-btn" onClick={handleContinueRide}>Continue Ride</button>
            ) : (
              <button className="request-ride-btn" onClick={handleSeePrice}>See price</button>
            )}
            <button className="explore-now-btn" onClick={() => setIsExploreModalOpen(true)}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8h1a4 4 0 0 1 0 8h-1"></path><path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"></path><line x1="6" y1="1" x2="6" y2="4"></line><line x1="10" y1="1" x2="10" y2="4"></line><line x1="14" y1="1" x2="14" y2="4"></line></svg>
              <span>Explore</span>
            </button>
          </div>
        </div>

        {currentUser && (
          <div className="ride-suggestions-card">
            <div className="suggestions-card-header">
              <div className="suggestions-header-left">
                <h2>Ways to move & eat</h2>
                <p className="suggestions-header-subtitle">Explore 9tro services</p>
              </div>
            </div>

            <div className="suggestions-grid-container">
              <div 
                className="service-card-item delivery-card"
                onClick={() => navigate('/package-delivery', { state: { pickup, dropoff } })}
              >
                <div className="service-card-top">
                  <div className="service-card-icon-badge delivery-icon-badge">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                      <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                      <line x1="12" y1="22.08" x2="12" y2="12"></line>
                    </svg>
                  </div>
                  <span className="service-chip-badge fast-chip">Fast</span>
                </div>
                <div className="service-card-body">
                  <h3 className="service-card-title">Package Delivery</h3>
                  <p className="service-card-sub">Send & receive items fast</p>
                </div>
                <div className="service-card-footer">
                  <span className="service-action-link">
                    View delivery
                    <svg className="action-arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                      <polyline points="12 5 19 12 12 19"></polyline>
                    </svg>
                  </span>
                </div>
              </div>

              <div 
                className="service-card-item food-card"
                onClick={() => navigate('/9troeat/vendors')}
              >
                <div className="service-card-top">
                  <div className="service-card-icon-badge food-icon-badge">
                    <span>🍔</span>
                  </div>
                  <span className="service-chip-badge popular-chip">Popular</span>
                </div>
                <div className="service-card-body">
                  <h3 className="service-card-title">Restaurants</h3>
                  <p className="service-card-sub">Order delicious local food</p>
                </div>
                <div className="service-card-footer">
                  <span className="service-action-link">
                    Order food
                    <svg className="action-arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                      <polyline points="12 5 19 12 12 19"></polyline>
                    </svg>
                  </span>
                </div>
              </div>

              <div 
                className="service-card-item chef-card"
                onClick={() => navigate('/9troeat/vendors')}
              >
                <div className="service-card-top">
                  <div className="service-card-icon-badge chef-icon-badge">
                    <span>👨‍🍳</span>
                  </div>
                  <span className="service-chip-badge luxury-chip">Exclusive</span>
                </div>
                <div className="service-card-body">
                  <h3 className="service-card-title">Private Chef</h3>
                  <p className="service-card-sub">Book gourmet custom dining</p>
                </div>
                <div className="service-card-footer">
                  <span className="service-action-link">
                    Book chef
                    <svg className="action-arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                      <polyline points="12 5 19 12 12 19"></polyline>
                    </svg>
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {isCityModalOpen && (
        <div className="location-modal-overlay" onClick={() => setIsCityModalOpen(false)}>
            <div className="location-modal city-search-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <div className="modal-title-group">
                        <h2>Your current city is</h2>
                        <span>{city}</span>
                    </div>
                    <button className="close-modal-btn" onClick={() => setIsCityModalOpen(false)}>×</button>
                </div>
                <div className="modal-content-body">
                    <div className="input-wrapper" style={{ marginBottom: 0 }}>
                        <div className="location-icon">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
                        </div>
                        <input
                            type="text"
                            className="location-input"
                            placeholder="Enter a city"
                            value={newCityInput}
                            onChange={(e) => handleInputChange(e, "city")}
                            onFocus={() => setActiveInput("city")}
                            autoFocus
                        />
                        {activeInput === "city" && cityPredictions.length > 0 && (
                            <ul className="predictions-dropdown">
                                {cityPredictions.map((p) => (
                                    <li key={p.place_id} className="prediction-item" onClick={() => handlePredictionSelect(p, "city")}>
                                        <div className="prediction-icon"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg></div>
                                        {p.structured_formatting ? (
                                            <div className="prediction-text">
                                                <span className="prediction-main-text">{p.structured_formatting.main_text}</span>
                                                <span className="prediction-secondary-text">{p.structured_formatting.secondary_text}</span>
                                            </div>
                                        ) : (
                                            <span>{p.description}</span>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    <div className="suggested-cities-section">
                        <h4 className="suggested-cities-title">Popular Cities</h4>
                        <div className="city-pills">
                            {["Lagos", "Abuja", "Ogun", "Ibadan"].map((cityName) => (
                                <button key={cityName} className="city-pill" onClick={() => handleSuggestedCitySelect(cityName)}>
                                    {cityName}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="modal-divider">
                        <span>or</span>
                    </div>
                    <div>
                        <button className="explore-btn" onClick={() => { setIsCityModalOpen(false); setIsExploreModalOpen(true); }}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path><line x1="3" y1="6" x2="21" y2="6"></line><path d="M16 10a4 4 0 0 1-8 0"></path></svg><span>Explore</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
      )}

      {isExploreModalOpen && (
        <div className="location-modal-overlay" onClick={handleCloseExploreModal}>
          <div className="location-modal explore-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header explore-modal-header">
              <div className="explore-header-text">
                <h2>Explore your destination</h2>
                <p>{`It takes an average of 4-10 minutes to get a ride within ${city || 'your current city'}, and on average those rides cost between NGN 3,808-NGN 12,327.`}</p>
              </div>
              <button className="close-modal-btn" onClick={handleCloseExploreModal}>×</button>
            </div>
            <div className="modal-content-body" style={{ padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', height: '100%' }}>
              <div className="explore-header-with-badges">
                <div className="explore-badges">
                  {exploreCategories.map(category => (
                    <button
                      key={category}
                      className={`explore-badge ${exploreCategory === category ? 'active' : ''}`}
                      onClick={() => setExploreCategory(category)}
                    >
                      {getCategoryIcon(category)}
                      <span>{category}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="explore-main-content">
                <div className="explore-places-list">
                  {placesStatus === 'LOADING' && <div className="explore-loading"><p>Loading...</p></div>}
                  {placesStatus === 'ERROR' && (
                    <div className="explore-no-results">
                      <p>Could not load attractions.</p>
                      {placesError && <p className="error-details">{placesError}</p>}
                    </div>
                  )}
                  {placesStatus === 'SUCCESS' && places.length === 0 && (
                    <div className="explore-no-results">
                      <p>No {exploreCategory.toLowerCase()} found nearby.</p>
                    </div>
                  )}
                  {placesStatus === 'SUCCESS' && places.map((place) => (
                    <div 
                      key={place.place_id} 
                      className={`explore-place-card ${selectedPlaceId === place.place_id ? 'selected' : ''}`} 
                      onClick={() => handlePlaceCardClick(place)}
                    >
                      <div className="place-card-image-wrapper">
                        {place.photoUrl ? (
                          <img src={place.photoUrl} alt={place.name} className="place-card-image" />
                        ) : (
                          <div className="place-card-image-fallback">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                              <circle cx="12" cy="10" r="3"></circle>
                            </svg>
                          </div>
                        )}
                        {place.rating && (
                          <div className="place-rating-badge">
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="#FFD700" stroke="#FFD700" strokeWidth="1">
                              <polygon points="12,2 15,9 22,9 17,14 19,21 12,17 5,21 7,14 2,9 9,9" />
                            </svg>
                            <span>{place.rating.toFixed(1)}</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="place-card-details">
                        <div className="place-card-header">
                          <h3 className="place-card-name" title={place.name}>{place.name}</h3>
                          <p className="place-card-address" title={place.vicinity}>{place.vicinity}</p>
                        </div>
                        
                        <div className="place-card-footer">
                          <button className="place-action-btn" onClick={(e) => handleExplorePlace(e, place)}>
                            <span>Set Destination</span>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <line x1="5" y1="12" x2="19" y2="12"></line>
                              <polyline points="12 5 19 12 12 19"></polyline>
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="explore-map-container" ref={mapRef} />
              </div>
            </div>
          </div>
        </div>
      )}

      {isLocationRequiredModalOpen && (
        <div className="location-modal-overlay" onClick={() => setIsLocationRequiredModalOpen(false)}>
          <div className="location-modal location-required-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header" style={{ padding: '24px 24px 16px 24px' }}>
              <div className="modal-title-group" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div className="warning-icon-wrapper">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#E3242B" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                    <line x1="12" y1="9" x2="12" y2="13"></line>
                    <line x1="12" y1="17" x2="12.01" y2="17"></line>
                  </svg>
                </div>
                <h2 style={{ fontSize: '20px', fontWeight: '700', margin: 0 }}>Locations Required</h2>
              </div>
              <button className="close-modal-btn" onClick={() => setIsLocationRequiredModalOpen(false)}>×</button>
            </div>
            <div className="modal-content-body" style={{ padding: '0 24px 24px 24px' }}>
              <div className="location-required-info">
                <p className="required-info-text">
                  To calculate the best route, estimate your fare, and check driver availability, we need both your <strong>pickup point</strong> and <strong>destination</strong>.
                </p>
                <div className="location-status-cards">
                  <div className={`status-card ${pickup ? 'filled' : 'empty'}`}>
                    <span className="status-dot pickup-dot"></span>
                    <div className="status-details">
                      <span className="status-label">Pickup Location</span>
                      <span className="status-value">{pickup || 'Not set yet'}</span>
                    </div>
                    {!pickup && <span className="status-badge">Required</span>}
                  </div>
                  <div className={`status-card ${dropoff ? 'filled' : 'empty'}`}>
                    <span className="status-dot dropoff-dot"></span>
                    <div className="status-details">
                      <span className="status-label">Destination Location</span>
                      <span className="status-value">{dropoff || 'Not set yet'}</span>
                    </div>
                    {!dropoff && <span className="status-badge">Required</span>}
                  </div>
                </div>
              </div>
              <button 
                className="confirm-location-btn" 
                onClick={() => {
                  setIsLocationRequiredModalOpen(false);
                  if (!pickup) {
                    const pickupInput = document.querySelector('input[placeholder*="pickup"]');
                    if (pickupInput) pickupInput.focus();
                  } else if (!dropoff) {
                    const dropoffInput = document.querySelector('input[placeholder*="destination"]');
                    if (dropoffInput) dropoffInput.focus();
                  }
                }}
              >
                Enter Address
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChooseRide;
